/**
 * BounceWave Quote Form Drawer — quote-form.js
 *
 * Vanilla JS IIFE. No dependencies.
 * Reads window.bwQuoteConfig set by snippets/quote-form-drawer.liquid.
 * Requires Cloudflare Turnstile loaded via the snippet's <script> tag.
 */
(function () {
  'use strict';

  // ── State ──────────────────────────────────────────────────────────────────
  var config            = window.bwQuoteConfig || {};
  var drawer            = null;
  var overlay           = null;
  var activeTrigger     = null;
  var focusTrapHandler  = null;
  var turnstileRendered = false;
  var turnstileToken    = null;
  var hasAttemptedSubmit = false;
  var zipDebounceTimer  = null;

  // ── Init ───────────────────────────────────────────────────────────────────

  function init() {
    drawer  = document.getElementById('bw-quote-drawer');
    overlay = document.getElementById('bw-quote-overlay');

    if (!drawer || !overlay) return;

    // Trigger buttons (there may be more than one on page in future)
    document.querySelectorAll('.bw-quote-trigger').forEach(function (btn) {
      btn.addEventListener('click', function () {
        activeTrigger = btn;
        populateProductStrip();
        openDrawer();
      });
    });

    // Overlay click closes drawer
    overlay.addEventListener('click', handleClose);

    // Header close button
    var closeBtn = qs('.bw-qd-close');
    if (closeBtn) closeBtn.addEventListener('click', handleClose);

    // Success state close button
    var successClose = qs('.bw-qd-btn--close-success');
    if (successClose) successClose.addEventListener('click', closeDrawer);

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        handleClose();
      }
    });

    // Quantity stepper
    bindQtyStepper();

    // Phone auto-format
    var phoneEl = document.getElementById('bw-phone');
    if (phoneEl) phoneEl.addEventListener('input', formatPhone);

    // ZIP auto-fill
    var zipEl = document.getElementById('bw-ship-zip');
    if (zipEl) {
      zipEl.addEventListener('input', function () {
        clearTimeout(zipDebounceTimer);
        var digits = this.value.replace(/\D/g, '');
        if (digits.length === 5) {
          zipDebounceTimer = setTimeout(function () { autoFillZip(digits); }, 400);
        }
      });
    }

    // Live validation: blur triggers single-field, input triggers if already attempted submit
    qsa('.bw-qd-input, .bw-qd-select, .bw-qd-textarea').forEach(function (el) {
      el.addEventListener('blur', function () {
        validateSingleInput(this);
        updateProgress();
      });
      el.addEventListener('input', function () {
        if (hasAttemptedSubmit) validateSingleInput(this);
        updateProgress();
      });
      el.addEventListener('change', function () {
        if (hasAttemptedSubmit) validateSingleInput(this);
        updateProgress();
      });
    });

    // Radio + checkbox change: re-validate groups if already attempted
    qsa('.bw-qd-radio, .bw-qd-checkbox').forEach(function (el) {
      el.addEventListener('change', function () {
        if (hasAttemptedSubmit) validateGroups();
        updateProgress();
      });
    });

    // Submit
    var submitBtn = document.getElementById('bw-quote-submit');
    if (submitBtn) submitBtn.addEventListener('click', handleSubmit);

    // Mobile swipe-to-close
    initSwipeClose();

    // Cache scrollbar width for body-lock padding compensation
    cacheScrollbarWidth();
  }

  // ── Product strip ──────────────────────────────────────────────────────────

  function populateProductStrip() {
    var nameEl = qs('.bw-qd-product-name');
    var imgEl  = qs('.bw-qd-product-img');
    var title  = config.productTitle || 'Quote Request';

    if (nameEl) nameEl.textContent = title;

    if (imgEl) {
      var src = config.productImage || '';
      if (src) {
        imgEl.src = src;
        imgEl.alt = title;
        imgEl.style.display = '';
      } else {
        imgEl.style.display = 'none';
      }
    }
  }

  // ── Open / Close ───────────────────────────────────────────────────────────

  function openDrawer() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-visible');
    document.body.classList.add('bw-drawer-open');
    hasAttemptedSubmit = false;

    // Lazy-render Turnstile on first open
    if (!turnstileRendered) renderTurnstile();

    // Focus first focusable input after CSS transition settles
    setTimeout(function () {
      var first = drawer.querySelector('.bw-qd-input, .bw-qd-select');
      if (first) first.focus();
    }, 300);

    // Enable focus trap
    focusTrapHandler = makeFocusTrap(drawer);
    drawer.addEventListener('keydown', focusTrapHandler);
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-visible');
    document.body.classList.remove('bw-drawer-open');

    if (focusTrapHandler) {
      drawer.removeEventListener('keydown', focusTrapHandler);
      focusTrapHandler = null;
    }

    // Return focus to whatever opened the drawer
    if (activeTrigger) {
      activeTrigger.focus();
      activeTrigger = null;
    }
  }

  function handleClose() {
    if (isFormDirty()) {
      if (!window.confirm('Leave without submitting your quote request?')) return;
    }
    closeDrawer();
  }

  function isFormDirty() {
    var inputs = qsa('.bw-qd-input, .bw-qd-textarea');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].id === 'bw-quantity') continue; // always has a value
      if ((inputs[i].value || '').trim() !== '') return true;
    }
    var selects = qsa('.bw-qd-select');
    for (var j = 0; j < selects.length; j++) {
      if (selects[j].value !== '') return true;
    }
    if (qsa('.bw-qd-radio:checked, .bw-contact-method:checked').length > 0) return true;
    return false;
  }

  // ── Focus trap ─────────────────────────────────────────────────────────────

  function makeFocusTrap(element) {
    return function (e) {
      if (e.key !== 'Tab') return;

      var focusable = Array.from(element.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
        'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function (el) {
        return !el.closest('[hidden]') && el.offsetParent !== null;
      });

      if (!focusable.length) return;

      var first = focusable[0];
      var last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };
  }

  // ── Mobile swipe-to-close ──────────────────────────────────────────────────

  function initSwipeClose() {
    var startX = 0, startY = 0;

    drawer.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    drawer.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - startX;
      var dy = Math.abs(e.changedTouches[0].clientY - startY);
      // Right swipe > 40% of drawer width, dominates vertical movement
      if (dx > drawer.offsetWidth * 0.4 && dy < 80) handleClose();
    }, { passive: true });
  }

  // ── Scrollbar width ────────────────────────────────────────────────────────

  function cacheScrollbarWidth() {
    var w = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--bw-scrollbar-w', w + 'px');
  }

  // ── Phone formatting ───────────────────────────────────────────────────────

  function formatPhone(e) {
    var raw = e.target.value.replace(/\D/g, '').substring(0, 10);
    var out = '';
    if (raw.length >= 1) out = '(' + raw.substring(0, 3);
    if (raw.length >= 4) out += ') ' + raw.substring(3, 6);
    if (raw.length >= 7) out += '-' + raw.substring(6, 10);
    if (raw.length < 1)  out = '';
    e.target.value = out;
  }

  // ── ZIP auto-fill (Zippopotam.us — free, no API key) ──────────────────────

  function autoFillZip(zip) {
    fetch('https://api.zippopotam.us/us/' + zip)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.places || !data.places[0]) return;
        var place = data.places[0];
        var cityEl  = document.getElementById('bw-ship-city');
        var stateEl = document.getElementById('bw-ship-state');
        if (cityEl  && !cityEl.value)  cityEl.value  = place['place name'];
        if (stateEl && !stateEl.value) stateEl.value = place['state abbreviation'];
        updateProgress();
      })
      .catch(function () { /* silent — user can type manually */ });
  }

  // ── Quantity stepper ───────────────────────────────────────────────────────

  function bindQtyStepper() {
    var dec   = document.getElementById('bw-qty-dec');
    var inc   = document.getElementById('bw-qty-inc');
    var input = document.getElementById('bw-quantity');
    if (!dec || !inc || !input) return;

    dec.addEventListener('click', function () {
      var v = parseInt(input.value) || 1;
      if (v > 1) { input.value = v - 1; updateProgress(); }
    });
    inc.addEventListener('click', function () {
      var v = parseInt(input.value) || 1;
      if (v < 99) { input.value = v + 1; updateProgress(); }
    });
    input.addEventListener('change', function () {
      var v = parseInt(this.value);
      if (isNaN(v) || v < 1) this.value = 1;
      if (v > 99)             this.value = 99;
      updateProgress();
    });
  }

  // ── Progress bar ───────────────────────────────────────────────────────────

  var PROGRESS_CHECKS = [
    function () { return filled('bw-first-name'); },
    function () { return filled('bw-last-name'); },
    function () { return validEmail(val('bw-email')); },
    function () { return (val('bw-phone').replace(/\D/g, '').length >= 10); },
    function () { return qsa('.bw-contact-method:checked').length > 0; },
    function () { return parseInt(val('bw-quantity')) >= 1; },
    function () { return !!qs('input[name="accessories_needed"]:checked'); },
    function () { return val('bw-shipping-destination') !== ''; },
    function () { return !!qs('input[name="purchase_urgency"]:checked'); },
    function () { return filled('bw-ship-street'); },
    function () { return filled('bw-ship-city'); },
    function () { return filled('bw-ship-state'); },
    function () { return val('bw-ship-zip').trim().length >= 3; },
    function () { return val('bw-referral-source') !== ''; },
  ];

  function updateProgress() {
    var bar = qs('.bw-qd-progress-bar');
    if (!bar) return;
    var done = PROGRESS_CHECKS.filter(function (fn) { try { return fn(); } catch (e) { return false; } }).length;
    bar.style.width = Math.round((done / PROGRESS_CHECKS.length) * 100) + '%';
  }

  // ── Validation helpers ─────────────────────────────────────────────────────

  function setInputError(inputId, errorId, message) {
    var errEl   = document.getElementById(errorId);
    var inputEl = document.getElementById(inputId);
    if (errEl) {
      errEl.textContent = message || '';
      errEl.hidden = !message;
    }
    if (inputEl) {
      inputEl.classList.toggle('is-invalid', !!message);
      if (message) inputEl.setAttribute('aria-invalid', 'true');
      else         inputEl.removeAttribute('aria-invalid');
    }
  }

  function setGroupError(fieldsetSelector, errorId, message) {
    var errEl = document.getElementById(errorId);
    if (errEl) {
      errEl.textContent = message || '';
      errEl.hidden = !message;
    }
    var fieldset = errEl ? errEl.closest('.bw-qd-fieldset') : null;
    if (fieldset) fieldset.classList.toggle('has-error', !!message);
  }

  function validateSingleInput(input) {
    var id = input.id;
    var v  = input.value.trim();

    switch (id) {
      case 'bw-email':
        if (!v)              { setInputError(id, id + '-error', 'Required'); return false; }
        if (!validEmail(v))  { setInputError(id, id + '-error', 'Enter a valid email address'); return false; }
        setInputError(id, id + '-error', null); return true;

      case 'bw-phone':
        if (!v)                                    { setInputError(id, id + '-error', 'Required'); return false; }
        if (v.replace(/\D/g, '').length < 10)      { setInputError(id, id + '-error', 'Enter a valid 10-digit phone number'); return false; }
        setInputError(id, id + '-error', null); return true;

      case 'bw-quantity':
        var qty = parseInt(input.value);
        if (isNaN(qty) || qty < 1) { setInputError(id, id + '-error', 'Enter a quantity of at least 1'); return false; }
        setInputError(id, id + '-error', null); return true;

      case 'bw-shipping-destination':
      case 'bw-referral-source':
        if (!v) { setInputError(id, id + '-error', 'Please select one'); return false; }
        setInputError(id, id + '-error', null); return true;

      default:
        if (!v) { setInputError(id, id + '-error', 'Required'); return false; }
        setInputError(id, id + '-error', null); return true;
    }
  }

  function validateGroups() {
    var ok = true;

    // Preferred contact methods (checkboxes — at least one required)
    var contactOk = qsa('.bw-contact-method:checked').length > 0;
    setGroupError('.bw-qd-fieldset', 'bw-contact-methods-error', contactOk ? null : 'Please select at least one');
    if (!contactOk) ok = false;

    // Accessories (radio)
    var accessoriesOk = !!qs('input[name="accessories_needed"]:checked');
    setGroupError('.bw-qd-fieldset', 'bw-accessories-error', accessoriesOk ? null : 'Please select an option');
    if (!accessoriesOk) ok = false;

    // Purchase urgency (radio)
    var urgencyOk = !!qs('input[name="purchase_urgency"]:checked');
    setGroupError('.bw-qd-fieldset', 'bw-urgency-error', urgencyOk ? null : 'Please select one');
    if (!urgencyOk) ok = false;

    return ok;
  }

  function validateAll() {
    var ok = true;

    var textFields = [
      'bw-first-name', 'bw-last-name', 'bw-email', 'bw-phone',
      'bw-quantity', 'bw-shipping-destination', 'bw-ship-street',
      'bw-ship-city', 'bw-ship-state', 'bw-ship-zip', 'bw-referral-source',
    ];
    textFields.forEach(function (id) {
      var el = document.getElementById(id);
      if (el && !validateSingleInput(el)) ok = false;
    });

    if (!validateGroups()) ok = false;

    if (!ok) scrollToFirstError();
    return ok;
  }

  function scrollToFirstError() {
    var first = drawer.querySelector('.is-invalid, .bw-qd-fieldset.has-error');
    if (!first) return;
    first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var focusTarget = first.closest('.bw-qd-field')
      ? first.closest('.bw-qd-field').querySelector('input:not([type="hidden"]), select, textarea')
      : first;
    if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 300);
  }

  // ── Collect form data ──────────────────────────────────────────────────────

  function getFormData() {
    var contactMethods = Array.from(
      drawer.querySelectorAll('.bw-contact-method:checked')
    ).map(function (cb) { return cb.value; });

    var accessoriesEl = qs('input[name="accessories_needed"]:checked');
    var urgencyEl     = qs('input[name="purchase_urgency"]:checked');

    return {
      product_shopify_id: String(config.productId || ''),
      product_name:       config.productTitle || '',
      product_sku:        config.variantSku   || null,
      unit_price:         Number(config.productPrice) || 0,
      quantity:           parseInt(val('bw-quantity')) || 1,

      first_name: val('bw-first-name'),
      last_name:  val('bw-last-name'),
      email:      val('bw-email').toLowerCase(),
      phone:      val('bw-phone'),
      preferred_contact_methods: contactMethods,

      accessories_needed:        accessoriesEl ? accessoriesEl.value : '',
      shipping_destination_type: val('bw-shipping-destination'),
      purchase_urgency:          urgencyEl ? urgencyEl.value : '',

      ship_street:  val('bw-ship-street'),
      ship_city:    val('bw-ship-city'),
      ship_state:   val('bw-ship-state'),
      ship_zip:     val('bw-ship-zip'),
      ship_country: val('bw-ship-country'),

      referral_source: val('bw-referral-source'),
      notes:           val('bw-notes').trim() || null,

      website:               val('bw-website'),       // honeypot
      cf_turnstile_response: turnstileToken || '',
    };
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  function handleSubmit() {
    hasAttemptedSubmit = true;

    if (!validateAll()) return;

    if (!turnstileToken) {
      showErrorBanner('Please complete the security check below the form.');
      var tsEl = qs('.bw-qd-turnstile');
      if (tsEl) tsEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    clearErrorBanner();
    setLoadingState(true);

    fetch(config.edgeFunctionUrl, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(getFormData()),
    })
      .then(function (r) { return r.json(); })
      .then(function (result) {
        if (result.success) {
          showSuccess(result.quote_number, val('bw-email').toLowerCase());
        } else {
          showErrorBanner(result.error || 'Something went wrong. Please try again.');
          setLoadingState(false);
          resetTurnstile();
        }
      })
      .catch(function () {
        showErrorBanner('Network error. Please check your connection or call us at 1-888-901-9283.');
        setLoadingState(false);
        resetTurnstile();
      });
  }

  function setLoadingState(loading) {
    var btn     = document.getElementById('bw-quote-submit');
    if (!btn) return;
    var textEl    = btn.querySelector('.bw-qd-btn-text');
    var spinnerEl = btn.querySelector('.bw-qd-btn-spinner');

    btn.classList.toggle('is-loading', loading);
    btn.disabled = loading;
    if (textEl)    textEl.textContent = loading ? 'Sending...' : 'Get My Quote';
    if (spinnerEl) spinnerEl.hidden   = !loading;

    qsa('.bw-qd-input, .bw-qd-select, .bw-qd-textarea, .bw-qd-radio, .bw-qd-checkbox, .bw-qd-qty-btn')
      .forEach(function (el) { el.disabled = loading; });
  }

  function showErrorBanner(message) {
    var banner = qs('.bw-qd-error-banner');
    var msgEl  = qs('.bw-qd-error-msg');
    if (banner) { banner.hidden = false; }
    if (msgEl)  msgEl.textContent = message;
    if (banner) banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function clearErrorBanner() {
    var banner = qs('.bw-qd-error-banner');
    if (banner) banner.hidden = true;
  }

  function showSuccess(quoteNumber, email) {
    var formWrap  = qs('.bw-qd-form-wrap');
    var successEl = qs('.bw-qd-success');
    var emailEl   = qs('.bw-qd-success-email');
    var qnEl      = qs('.bw-qd-success-quotenum');
    var bodyEl    = qs('.bw-qd-body');

    if (formWrap)  formWrap.hidden  = true;
    if (successEl) successEl.hidden = false;
    if (emailEl)   emailEl.textContent  = email || '';
    if (qnEl && quoteNumber) qnEl.textContent = 'Quote ' + quoteNumber;
    if (bodyEl)    bodyEl.scrollTop = 0;

    var closeBtn = qs('.bw-qd-btn--close-success');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 60);
  }

  // ── Turnstile ──────────────────────────────────────────────────────────────

  function renderTurnstile() {
    var siteKey = config.turnstiteSiteKey || '';
    if (!siteKey || siteKey === 'PASTE_YOUR_TURNSTILE_SITE_KEY_HERE') return;

    function attempt() {
      if (typeof window.turnstile !== 'undefined') {
        try {
          window.turnstile.render('#bw-turnstile-container', {
            sitekey: siteKey,
            theme:   'dark',
            callback: function (token) {
              turnstileToken = token;
            },
            'error-callback': function () {
              turnstileToken = null;
            },
            'expired-callback': function () {
              turnstileToken = null;
            },
          });
          turnstileRendered = true;
        } catch (e) {
          console.warn('[BW Quote] Turnstile render error:', e);
        }
      } else {
        // Script not yet ready — retry after 300ms
        setTimeout(attempt, 300);
      }
    }
    attempt();
  }

  function resetTurnstile() {
    turnstileToken    = null;
    turnstileRendered = false;
    var container = document.getElementById('bw-turnstile-container');
    if (container) container.innerHTML = '';
    renderTurnstile();
  }

  // ── Small helpers ──────────────────────────────────────────────────────────

  function qs(selector)  { return drawer ? drawer.querySelector(selector) : null; }
  function qsa(selector) { return drawer ? Array.from(drawer.querySelectorAll(selector)) : []; }

  function val(id) {
    var el = document.getElementById(id);
    return el ? el.value.trim() : '';
  }
  function filled(id) { return val(id).length > 0; }
  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

  // ── Boot ───────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
